import { initSentry } from './sentry.js';

// Initialize Sentry first
initSentry();

import type { User } from '@flipboard/shared';
import {
  getCurrentUser,
  getGoogleLoginUrl,
  logout,
  getBoards,
  createBoard,
  updateBoard,
  sendMessage,
  deleteBoard,
  getDisplayUrl,
  getBoardState,
  getViewerCount,
  toggleSound,
  getApiKeys,
  createApiKey,
  deleteApiKey,
  createCheckoutSession,
  createPortalSession,
  getAdminMetrics,
  getAdminUsers,
  getAdminUserDetail,
  updateAdminUserSubscription,
  deleteAdminUserBoard,
  deleteAdminUserApiKey,
  impersonateUser,
  deleteAdminUser,
  getPlaylists,
  createPlaylist,
  getPlaylist,
  updatePlaylist,
  deletePlaylist,
  addPlaylistItem,
  updatePlaylistItem,
  removePlaylistItem,
  reorderPlaylistItems,
  getPlaylistDisplayUrl,
  type Board,
  type ApiKey,
  type AdminUser,
  type AdminUserDetail,
  type Playlist,
  type PlaylistWithItems,
} from './api.js';
import { BoardEditor, ColorToolbar } from './board-editor.js';
import { drawQRCode } from './qr-code.js';
import { toast } from './toast.js';
import { initCookieConsent } from './cookie-consent.js';

// State
let currentUser: User | null = null;
let boards: Board[] = [];
let currentBoard: Board | null = null;
let playlists: Playlist[] = [];
let currentPlaylist: PlaylistWithItems | null = null;
let apiKeys: ApiKey[] = [];
let currentAdminUser: AdminUserDetail | null = null;

// Board Editor instance
let boardEditor: BoardEditor | null = null;
let colorToolbar: ColorToolbar | null = null;

// Track viewer count refresh interval
let viewerCountInterval: number | null = null;

// Views
const loginView = document.getElementById('login-view')!;
const appShell = document.getElementById('app-shell')!;
const boardsView = document.getElementById('boards-view')!;
const boardEditorView = document.getElementById('board-editor-view')!;
const playlistsView = document.getElementById('playlists-view')!;
const playlistEditorView = document.getElementById('playlist-editor-view')!;
const apiKeysView = document.getElementById('api-keys-view')!;
const subscriptionView = document.getElementById('subscription-view')!;
const adminView = document.getElementById('admin-view')!;
const adminUserView = document.getElementById('admin-user-view')!;

// Login
const loginBtn = document.getElementById('login-btn')!;

// App Header
const mobileMenuBtn = document.getElementById('mobile-menu-btn')!;
const userMenuBtn = document.getElementById('user-menu-btn')!;
const userDropdown = document.getElementById('user-dropdown')!;
const userAvatar = document.getElementById('user-avatar') as HTMLImageElement;
const userName = document.getElementById('user-name')!;
const logoutBtn = document.getElementById('logout-btn')!;

// Sidebar
const appSidebar = document.getElementById('app-sidebar')!;
const sidebarBackdrop = document.getElementById('sidebar-backdrop')!;

// Boards view
const createBoardBtn = document.getElementById('create-board-btn')!;
const emptyCreateBtn = document.getElementById('empty-create-btn')!;
const boardsList = document.getElementById('boards-list')!;
const emptyState = document.getElementById('empty-state')!;

// Playlists view
const createPlaylistBtn = document.getElementById('create-playlist-btn')!;
const emptyCreatePlaylistBtn = document.getElementById('empty-create-playlist-btn')!;
const playlistsList = document.getElementById('playlists-list')!;
const playlistsEmpty = document.getElementById('playlists-empty-state')!;

// Playlist editor view
const playlistBackBtn = document.getElementById('playlist-back-btn')!;
const playlistTitle = document.getElementById('playlist-title')!;
const openPlaylistDisplayBtn = document.getElementById('open-playlist-display-btn')!;
const playlistSettingsBtn = document.getElementById('playlist-settings-btn')!;
const playlistItemsList = document.getElementById('playlist-items')!;
const playlistItemsEmpty = document.getElementById('playlist-empty-items')!;
const addBoardBtn = document.getElementById('add-board-to-playlist-btn')!;

// Playlist settings drawer
const playlistSettingsBackdrop = document.getElementById('playlist-settings-backdrop')!;
const playlistSettingsDrawer = document.getElementById('playlist-settings-drawer')!;
const closePlaylistSettingsBtn = document.getElementById('close-playlist-settings-btn')!;
const playlistUrlDisplay = document.getElementById('playlist-url-display')!;
const copyPlaylistUrlBtn = document.getElementById('copy-playlist-url-btn')!;
const playlistNameInput = document.getElementById('playlist-name-input') as HTMLInputElement;
const updatePlaylistNameBtn = document.getElementById('update-playlist-name-btn')!;
const playlistRowsInput = document.getElementById('playlist-rows') as HTMLInputElement;
const playlistColsInput = document.getElementById('playlist-cols') as HTMLInputElement;
const updatePlaylistSizeBtn = document.getElementById('update-playlist-size-btn')!;
// const clearPlaylistSizeBtn = document.getElementById('clear-playlist-size-btn')!; // Doesn't exist in HTML
const playlistFillScreenSelect = document.getElementById('playlist-fill-screen-select') as HTMLSelectElement;
const playlistSoundSelect = document.getElementById('playlist-sound-select') as HTMLSelectElement;
const playlistDefaultDurationInput = document.getElementById('playlist-duration') as HTMLInputElement;
const updatePlaylistDurationBtn = document.getElementById('update-playlist-duration-btn')!;
const playlistShuffleToggle = document.getElementById('playlist-shuffle-toggle') as HTMLInputElement;
const deletePlaylistBtn = document.getElementById('delete-playlist-btn')!;

// Add Board modal
const addBoardModal = document.getElementById('add-board-modal')!;
const boardsSelectionList = document.getElementById('available-boards-list')!;
const cancelAddBoardBtn = document.getElementById('cancel-add-board-btn')!;
const confirmAddBoardBtn = document.getElementById('confirm-add-board-btn')!;

// Board editor view
const backBtn = document.getElementById('back-btn')!;
const boardTitle = document.getElementById('board-title')!;
const boardViewerCountValue = document.getElementById('board-viewer-count-value')!;
const openDisplayBtn = document.getElementById('open-display-btn')!;
const settingsBtn = document.getElementById('settings-btn')!;
const boardEditorContainer = document.getElementById('board-editor')!;
const colorToolbarContainer = document.getElementById('color-toolbar')!;
const clearCheckbox = document.getElementById('clear-checkbox') as HTMLInputElement;
const sendMessageBtn = document.getElementById('send-message-btn')!;

// Settings drawer
const settingsBackdrop = document.getElementById('settings-backdrop')!;
const settingsDrawer = document.getElementById('settings-drawer')!;
const closeSettingsBtn = document.getElementById('close-settings-btn')!;
const boardUrlDisplay = document.getElementById('board-url-display')!;
const copyUrlBtn = document.getElementById('copy-url-btn')!;
const qrUrlBtn = document.getElementById('qr-url-btn')!;
const boardNameSettingInput = document.getElementById('board-name-input') as HTMLInputElement;
const updateNameBtn = document.getElementById('update-name-btn')!;
const boardRowsInput = document.getElementById('board-rows') as HTMLInputElement;
const boardColsInput = document.getElementById('board-cols') as HTMLInputElement;
const updateSizeBtn = document.getElementById('update-size-btn')!;
const fillScreenToggle = document.getElementById('fill-screen-toggle') as HTMLInputElement;
const soundToggle = document.getElementById('sound-toggle') as HTMLInputElement;
const deleteBoardBtn = document.getElementById('delete-board-btn')!;

// Modals
const qrModal = document.getElementById('qr-modal')!;
const qrCanvas = document.getElementById('qr-canvas') as HTMLCanvasElement;
const qrUrlText = document.getElementById('qr-url-text')!;
const closeQrBtn = document.getElementById('close-qr-btn')!;
const createModal = document.getElementById('create-modal')!;
const boardNameInput = document.getElementById('board-name') as HTMLInputElement;
const createBoardRowsInput = document.getElementById('create-board-rows') as HTMLInputElement;
const createBoardColsInput = document.getElementById('create-board-cols') as HTMLInputElement;
const cancelCreateBtn = document.getElementById('cancel-create-btn')!;
const confirmCreateBtn = document.getElementById('confirm-create-btn')!;

// API Keys view
const createApiKeyBtn = document.getElementById('create-api-key-btn')!;
const emptyCreateApiKeyBtn = document.getElementById('empty-create-api-key-btn');
const apiKeysList = document.getElementById('api-keys-list')!;
const apiKeysEmpty = document.getElementById('api-keys-empty')!;
const createApiKeyModal = document.getElementById('create-api-key-modal')!;
const apiKeyNameInput = document.getElementById('api-key-name') as HTMLInputElement;
const scopeReadCheckbox = document.getElementById('scope-read') as HTMLInputElement;
const scopeWriteCheckbox = document.getElementById('scope-write') as HTMLInputElement;
const cancelCreateApiKeyBtn = document.getElementById('cancel-api-key-btn')!;
const confirmCreateApiKeyBtn = document.getElementById('confirm-api-key-btn')!;
const apiKeyCreatedModal = document.getElementById('api-key-created-modal')!;
const apiKeyDisplay = document.getElementById('new-api-key-value')!;
const copyApiKeyBtn = document.getElementById('copy-api-key-btn')!;
const closeApiKeyCreatedBtn = document.getElementById('close-api-key-created-btn')!;

// Admin User Detail view
const adminUserBackBtn = document.getElementById('admin-user-back-btn')!;
const adminUserAvatar = document.getElementById('admin-user-avatar') as HTMLImageElement;
const adminUserName = document.getElementById('admin-user-name')!;
const adminUserEmail = document.getElementById('admin-user-email')!;
const adminUserCreated = document.getElementById('admin-user-created')!;
const adminImpersonateBtn = document.getElementById('admin-impersonate-btn')!;
const adminDeleteUserBtn = document.getElementById('admin-delete-user-btn')!;
const adminEditSubscriptionBtn = document.getElementById('admin-edit-subscription-btn')!;
const adminUserTier = document.getElementById('admin-user-tier')!;
const adminUserStatus = document.getElementById('admin-user-status')!;
const adminUserBillingCycle = document.getElementById('admin-user-billing-cycle')!;
const adminUserPeriodEnd = document.getElementById('admin-user-period-end')!;
const adminUserStripeId = document.getElementById('admin-user-stripe-id')!;
const adminUserBoardsCount = document.getElementById('admin-user-boards-count')!;
const adminUserBoardsList = document.getElementById('admin-user-boards-list')!;
const adminUserBoardsEmpty = document.getElementById('admin-user-boards-empty')!;
const adminUserApikeysCount = document.getElementById('admin-user-apikeys-count')!;
const adminUserApikeysList = document.getElementById('admin-user-apikeys-list')!;
const adminUserApikeysEmpty = document.getElementById('admin-user-apikeys-empty')!;

// Edit Subscription Modal
const editSubscriptionModal = document.getElementById('edit-subscription-modal')!;
const editTierSelect = document.getElementById('edit-tier') as HTMLSelectElement;
const editStatusSelect = document.getElementById('edit-status') as HTMLSelectElement;
const editPeriodEndInput = document.getElementById('edit-period-end') as HTMLInputElement;
const cancelEditSubscriptionBtn = document.getElementById('cancel-edit-subscription-btn')!;
const confirmEditSubscriptionBtn = document.getElementById('confirm-edit-subscription-btn')!;

// Impersonation Banner
const impersonationBanner = document.getElementById('impersonation-banner')!;
const impersonationUserName = document.getElementById('impersonation-user-name')!;
const endImpersonationBtn = document.getElementById('end-impersonation-btn')!;

// View management
function showView(view: 'login' | 'boards' | 'editor' | 'playlists' | 'playlist-editor' | 'apikeys' | 'subscription' | 'admin' | 'admin-user') {
  loginView.classList.toggle('hidden', view !== 'login');
  appShell.classList.toggle('hidden', view === 'login');
  boardsView.classList.toggle('hidden', view !== 'boards');
  boardEditorView.classList.toggle('hidden', view !== 'editor');
  playlistsView.classList.toggle('hidden', view !== 'playlists');
  playlistEditorView.classList.toggle('hidden', view !== 'playlist-editor');
  apiKeysView.classList.toggle('hidden', view !== 'apikeys');
  subscriptionView.classList.toggle('hidden', view !== 'subscription');
  adminView.classList.toggle('hidden', view !== 'admin');
  adminUserView.classList.toggle('hidden', view !== 'admin-user');

  // Update sidebar active state
  document.querySelectorAll('.nav-item').forEach((item) => {
    const navType = item.getAttribute('data-nav');
    // Admin user view should also highlight the admin nav item
    const isAdminRelated = view === 'admin' || view === 'admin-user';
    // Playlist editor view should also highlight the playlists nav item
    const isPlaylistRelated = view === 'playlists' || view === 'playlist-editor';
    item.classList.toggle('active', navType === view || (navType === 'admin' && isAdminRelated) || (navType === 'playlists' && isPlaylistRelated));
  });

  // Close settings drawer when switching views
  if (view !== 'editor') {
    closeSettings();
  }

  // Close playlist settings drawer when switching views
  if (view !== 'playlist-editor') {
    closePlaylistSettings();
  }

  // Load admin data when switching to admin view
  if (view === 'admin') {
    loadAdminView();
  }
}

// Settings drawer
function openSettings() {
  settingsBackdrop.classList.remove('hidden');
  settingsDrawer.classList.remove('hidden');
}

function closeSettings() {
  settingsBackdrop.classList.add('hidden');
  settingsDrawer.classList.add('hidden');
}

// Playlist settings drawer
function openPlaylistSettings() {
  playlistSettingsBackdrop.classList.remove('hidden');
  playlistSettingsDrawer.classList.remove('hidden');
}

function closePlaylistSettings() {
  playlistSettingsBackdrop.classList.add('hidden');
  playlistSettingsDrawer.classList.add('hidden');
}

// User menu
function toggleUserMenu() {
  userDropdown.classList.toggle('hidden');
}

function closeUserMenu() {
  userDropdown.classList.add('hidden');
}

// Mobile sidebar
function openMobileSidebar() {
  appSidebar.classList.add('open');
  sidebarBackdrop.classList.remove('hidden');
}

function closeMobileSidebar() {
  appSidebar.classList.remove('open');
  sidebarBackdrop.classList.add('hidden');
}

// Modal
function showModal(show: boolean) {
  createModal.classList.toggle('hidden', !show);
  if (show) {
    boardNameInput.value = '';
    createBoardRowsInput.value = '6';
    createBoardColsInput.value = '22';

    // Disable board size controls for free users
    const createBoardSizeGroup = document.querySelector('#create-modal .form-group:has(#create-board-rows)');
    const createSizeBadge = document.getElementById('create-board-size-premium-badge');
    const createSizeHint = document.getElementById('create-board-size-hint');

    if (currentUser && currentUser.subscriptionTier === 'free') {
      createBoardSizeGroup?.classList.add('premium-disabled');
      createSizeBadge?.classList.remove('hidden');
      createSizeHint?.classList.remove('hidden');
    } else {
      createBoardSizeGroup?.classList.remove('premium-disabled');
      createSizeBadge?.classList.add('hidden');
      createSizeHint?.classList.add('hidden');
    }
  }
}

// Render boards list
async function renderBoards() {
  if (boards.length === 0) {
    boardsList.classList.add('hidden');
    emptyState.classList.remove('hidden');
    return;
  }

  boardsList.classList.remove('hidden');
  emptyState.classList.add('hidden');

  // Fetch viewer counts for all boards in parallel
  const token = getStoredToken();
  const viewerCounts = await Promise.all(
    boards.map(async (board) => {
      try {
        const count = await getViewerCount(board.id, token);
        return { id: board.id, viewers: count };
      } catch {
        return { id: board.id, viewers: 0 };
      }
    })
  );

  // Update boards with viewer counts
  viewerCounts.forEach(({ id, viewers }) => {
    const board = boards.find((b) => b.id === id);
    if (board) board.viewers = viewers;
  });

  boardsList.innerHTML = boards
    .map(
      (board) => `
    <div class="board-card" data-board-id="${board.id}">
      <div class="board-card-header">
        <h3>${board.name || 'Untitled Board'}</h3>
        <div class="board-id">${board.id}</div>
      </div>
      <div class="board-meta">
        <span class="size-indicator">${board.rows}×${board.cols}</span>
        <span class="viewer-count" title="${board.viewers || 0} active viewer${board.viewers === 1 ? '' : 's'}">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
          ${board.viewers || 0}
        </span>
      </div>
    </div>
  `
    )
    .join('');

  // Add click handlers
  boardsList.querySelectorAll('.board-card').forEach((card) => {
    card.addEventListener('click', () => {
      const boardId = card.getAttribute('data-board-id')!;
      openBoard(boardId);
    });
  });
}

// Render API keys list
function renderApiKeys() {
  if (apiKeys.length === 0) {
    apiKeysList.classList.add('hidden');
    apiKeysEmpty.classList.remove('hidden');
    return;
  }

  apiKeysList.classList.remove('hidden');
  apiKeysEmpty.classList.add('hidden');

  apiKeysList.innerHTML = apiKeys
    .map(
      (key) => `
    <div class="api-key-card">
      <div class="api-key-header">
        <div>
          <h3>${key.name}</h3>
          <code class="key-prefix">${key.prefix}...</code>
        </div>
        <button class="btn-icon-only delete-key-btn" data-key-id="${key.id}" title="Delete API key">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14z"/>
          </svg>
        </button>
      </div>
      <div class="api-key-meta">
        ${key.scopes.map(scope => `<span class="scope-badge ${scope}">${scope}</span>`).join('')}
        <span class="last-used">${key.lastUsedAt ? `Last used ${formatDate(key.lastUsedAt)}` : 'Never used'}</span>
      </div>
    </div>
  `
    )
    .join('');

  // Add delete handlers
  apiKeysList.querySelectorAll('.delete-key-btn').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const keyId = (btn as HTMLElement).getAttribute('data-key-id')!;
      const key = apiKeys.find((k) => k.id === keyId);
      if (!key) return;

      if (!confirm(`Are you sure you want to delete the API key "${key.name}"? This action cannot be undone.`)) {
        return;
      }

      try {
        const token = getStoredToken();
        await deleteApiKey(keyId, token);
        apiKeys = apiKeys.filter((k) => k.id !== keyId);
        renderApiKeys();
      } catch (error) {
        console.error('Delete API key error:', error);
        toast.error('Failed to delete API key');
      }
    });
  });
}

// Render playlists list
function renderPlaylists() {
  if (playlists.length === 0) {
    playlistsList.classList.add('hidden');
    playlistsEmpty.classList.remove('hidden');
    return;
  }

  playlistsList.classList.remove('hidden');
  playlistsEmpty.classList.add('hidden');

  playlistsList.innerHTML = playlists
    .map(
      (playlist) => `
    <div class="board-card" data-playlist-id="${playlist.id}">
      <div class="board-card-header">
        <h3>${playlist.name || 'Untitled Playlist'}</h3>
        <div class="board-id">${playlist.id}</div>
      </div>
      <div class="board-meta">
        <span class="size-indicator">${playlist.itemCount || 0} ${playlist.itemCount === 1 ? 'board' : 'boards'}</span>
        <span class="viewer-count" title="0 active viewers">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
          0
        </span>
      </div>
    </div>
  `
    )
    .join('');

  // Add click handlers
  playlistsList.querySelectorAll('.board-card').forEach((card) => {
    card.addEventListener('click', () => {
      const playlistId = card.getAttribute('data-playlist-id')!;
      openPlaylist(playlistId);
    });
  });
}

// Open playlist editor view
async function openPlaylist(playlistId: string) {
  const playlist = playlists.find((p) => p.id === playlistId);
  if (!playlist) return;

  try {
    const token = getStoredToken();
    currentPlaylist = await getPlaylist(playlistId, token);

    playlistTitle.textContent = currentPlaylist.name || 'Untitled Playlist';
    playlistUrlDisplay.textContent = getPlaylistDisplayUrl(currentPlaylist.id);
    playlistNameInput.value = currentPlaylist.name || '';

    // Set size inputs (null = use board's size)
    playlistRowsInput.value = currentPlaylist.rows !== null ? String(currentPlaylist.rows) : '';
    playlistColsInput.value = currentPlaylist.cols !== null ? String(currentPlaylist.cols) : '';

    // Set fillScreen select (null = use board's setting)
    playlistFillScreenSelect.value = currentPlaylist.fillScreen === null ? '' : String(currentPlaylist.fillScreen);

    // Set sound select (null = use board's setting)
    playlistSoundSelect.value = currentPlaylist.soundEnabled === null ? '' : String(currentPlaylist.soundEnabled);

    // Set duration and shuffle
    playlistDefaultDurationInput.value = String(currentPlaylist.defaultDuration);
    playlistShuffleToggle.checked = currentPlaylist.shuffle;

    renderPlaylistItems();
    showView('playlist-editor');
  } catch (error) {
    console.error('Failed to load playlist:', error);
    toast.error('Failed to load playlist');
  }
}

// Render playlist items
function renderPlaylistItems() {
  if (!currentPlaylist) return;

  if (currentPlaylist.items.length === 0) {
    playlistItemsList.classList.add('hidden');
    playlistItemsEmpty.classList.remove('hidden');
    return;
  }

  playlistItemsList.classList.remove('hidden');
  playlistItemsEmpty.classList.add('hidden');

  playlistItemsList.innerHTML = currentPlaylist.items
    .sort((a, b) => a.position - b.position)
    .map(
      (item) => `
    <div class="playlist-item" draggable="true" data-item-id="${item.id}" data-position="${item.position}">
      <div class="playlist-item-drag-handle">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M3 8h18M3 16h18"/>
        </svg>
      </div>
      <div class="playlist-item-content">
        <div class="playlist-item-info">
          <h4>${item.board?.name || 'Untitled Board'}</h4>
          <span class="playlist-item-meta">${item.board?.rows}×${item.board?.cols}</span>
        </div>
        <div class="playlist-item-duration">
          <input type="number" class="duration-input" data-item-id="${item.id}" value="${item.duration !== null ? item.duration : currentPlaylist!.defaultDuration}" min="5" max="3600" />
          <span>seconds</span>
        </div>
      </div>
      <button class="btn-icon-only edit-board-btn" data-board-id="${item.boardId}" title="Edit board">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
        </svg>
      </button>
      <button class="btn-icon-only remove-item-btn" data-item-id="${item.id}" title="Remove board">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M18 6L6 18M6 6l12 12"/>
        </svg>
      </button>
    </div>
  `
    )
    .join('');

  // Add edit board handlers
  playlistItemsList.querySelectorAll('.edit-board-btn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const boardId = (btn as HTMLElement).getAttribute('data-board-id')!;
      openBoard(boardId);
    });
  });

  // Add remove handlers
  playlistItemsList.querySelectorAll('.remove-item-btn').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const itemId = (btn as HTMLElement).getAttribute('data-item-id')!;
      await handleRemovePlaylistItem(itemId);
    });
  });

  // Add duration change handlers
  playlistItemsList.querySelectorAll('.duration-input').forEach((input) => {
    input.addEventListener('change', async () => {
      const itemId = (input as HTMLInputElement).getAttribute('data-item-id')!;
      const duration = parseInt((input as HTMLInputElement).value, 10);
      if (duration >= 5 && duration <= 3600) {
        await handleUpdateItemDuration(itemId, duration);
      } else {
        toast.error('Duration must be between 5 and 3600 seconds');
        // Reset to current value
        const item = currentPlaylist?.items.find((i) => i.id === itemId);
        if (item && currentPlaylist) {
          (input as HTMLInputElement).value = String(item.duration !== null ? item.duration : currentPlaylist.defaultDuration);
        }
      }
    });
  });

  // Initialize drag-and-drop
  initPlaylistDragAndDrop();
}

// Initialize drag-and-drop for playlist items
let draggedItem: HTMLElement | null = null;
let dropListenerAdded = false;

function initPlaylistDragAndDrop() {
  const items = playlistItemsList.querySelectorAll('.playlist-item');

  items.forEach((item) => {
    item.addEventListener('dragstart', (e: Event) => {
      const dragEvent = e as DragEvent;
      draggedItem = item as HTMLElement;
      (item as HTMLElement).classList.add('dragging');
      if (dragEvent.dataTransfer) {
        dragEvent.dataTransfer.effectAllowed = 'move';
      }
    });

    item.addEventListener('dragend', () => {
      (item as HTMLElement).classList.remove('dragging');
      draggedItem = null;
    });

    item.addEventListener('dragover', (e: Event) => {
      const dragEvent = e as DragEvent;
      dragEvent.preventDefault();
      if (!draggedItem || draggedItem === item) return;

      const rect = (item as HTMLElement).getBoundingClientRect();
      const midpoint = rect.top + rect.height / 2;

      if (dragEvent.clientY < midpoint) {
        item.parentNode?.insertBefore(draggedItem, item);
      } else {
        item.parentNode?.insertBefore(draggedItem, item.nextSibling);
      }
    });
  });

  // Only add drop listener once to prevent multiple calls
  if (!dropListenerAdded) {
    playlistItemsList.addEventListener('drop', async (e: Event) => {
      e.preventDefault();
      await handleReorderPlaylistItems();
    });
    dropListenerAdded = true;
  }
}

// Handle reorder playlist items
async function handleReorderPlaylistItems() {
  if (!currentPlaylist) return;

  const items = Array.from(playlistItemsList.querySelectorAll('.playlist-item'));
  const order = items
    .map((item, index) => {
      const itemId = (item as HTMLElement).getAttribute('data-item-id');
      if (!itemId) {
        console.error('Playlist item missing data-item-id:', item);
        return null;
      }
      return {
        itemId,
        position: index,
      };
    })
    .filter((item): item is { itemId: string; position: number } => item !== null);

  if (order.length === 0) {
    console.error('No valid items to reorder');
    return;
  }

  if (order.length !== currentPlaylist.items.length) {
    console.error('Order length mismatch:', order.length, 'vs', currentPlaylist.items.length);
    toast.error('Failed to reorder items: data mismatch');
    await openPlaylist(currentPlaylist.id);
    return;
  }

  try {
    console.log('Reordering playlist items:', order);
    const token = getStoredToken();
    await reorderPlaylistItems(currentPlaylist.id, order, token);

    // Update local state
    currentPlaylist.items = currentPlaylist.items.map((item) => {
      const newPos = order.find((o) => o.itemId === item.id);
      return newPos ? { ...item, position: newPos.position } : item;
    });

    toast.success('Playlist reordered');
  } catch (error) {
    console.error('Failed to reorder items:', error);
    toast.error(error instanceof Error ? error.message : 'Failed to reorder items');
    // Reload to get correct order
    if (currentPlaylist) {
      await openPlaylist(currentPlaylist.id);
    }
  }
}

// Handle remove playlist item
async function handleRemovePlaylistItem(itemId: string) {
  if (!currentPlaylist) return;

  const item = currentPlaylist.items.find((i) => i.id === itemId);
  if (!item) return;

  if (!confirm(`Remove "${item.board?.name || 'this board'}" from the playlist?`)) return;

  try {
    const token = getStoredToken();
    await removePlaylistItem(currentPlaylist.id, itemId, token);

    // Update local state
    currentPlaylist.items = currentPlaylist.items.filter((i) => i.id !== itemId);

    // Update playlist count
    const playlistIndex = playlists.findIndex((p) => p.id === currentPlaylist!.id);
    if (playlistIndex !== -1) {
      playlists[playlistIndex].itemCount = currentPlaylist.items.length;
    }

    renderPlaylistItems();
    renderPlaylists();
  } catch (error) {
    console.error('Failed to remove item:', error);
    toast.error('Failed to remove item');
  }
}

// Handle update item duration
async function handleUpdateItemDuration(itemId: string, duration: number) {
  if (!currentPlaylist) return;

  try {
    const token = getStoredToken();
    await updatePlaylistItem(currentPlaylist.id, itemId, duration, token);

    // Update local state
    const item = currentPlaylist.items.find((i) => i.id === itemId);
    if (item) {
      item.duration = duration;
    }
  } catch (error) {
    console.error('Failed to update duration:', error);
    toast.error('Failed to update duration');
  }
}

// Show add board modal
let selectedBoardsForPlaylist: Set<string> = new Set();

function showAddBoardModal(show: boolean) {
  addBoardModal.classList.toggle('hidden', !show);
  if (show) {
    selectedBoardsForPlaylist.clear();
    renderBoardsSelection();
  }
}

// Render boards selection list
function renderBoardsSelection() {
  if (!currentPlaylist) return;

  // Filter out boards already in the playlist
  const existingBoardIds = new Set(currentPlaylist.items.map((item) => item.boardId));
  const availableBoards = boards.filter((board) => !existingBoardIds.has(board.id));

  if (availableBoards.length === 0) {
    boardsSelectionList.innerHTML = '<div class="empty-state-inline">All your boards are already in this playlist.</div>';
    (confirmAddBoardBtn as HTMLButtonElement).disabled = true;
    return;
  }

  boardsSelectionList.innerHTML = availableBoards
    .map(
      (board) => `
    <div class="board-selection-item" data-board-id="${board.id}">
      <input type="checkbox" id="board-select-${board.id}" class="board-checkbox" />
      <label for="board-select-${board.id}">
        <h4>${board.name || 'Untitled Board'}</h4>
        <span class="board-size">${board.rows}×${board.cols}</span>
      </label>
    </div>
  `
    )
    .join('');

  // Add change handlers
  boardsSelectionList.querySelectorAll('.board-checkbox').forEach((checkbox) => {
    checkbox.addEventListener('change', () => {
      const boardId = (checkbox as HTMLInputElement).id.replace('board-select-', '');
      if ((checkbox as HTMLInputElement).checked) {
        selectedBoardsForPlaylist.add(boardId);
      } else {
        selectedBoardsForPlaylist.delete(boardId);
      }
      (confirmAddBoardBtn as HTMLButtonElement).disabled = selectedBoardsForPlaylist.size === 0;
    });
  });

  (confirmAddBoardBtn as HTMLButtonElement).disabled = true;
}

// Handle add boards to playlist
async function handleAddBoardsToPlaylist() {
  if (!currentPlaylist || selectedBoardsForPlaylist.size === 0) return;

  confirmAddBoardBtn.classList.add('loading');
  try {
    const token = getStoredToken();

    // Add boards in sequence
    for (const boardId of selectedBoardsForPlaylist) {
      const item = await addPlaylistItem(currentPlaylist.id, boardId, null, token);
      // Enrich item with board data from local boards array
      const board = boards.find((b) => b.id === boardId);
      if (board) {
        item.board = {
          id: board.id,
          name: board.name,
          rows: board.rows,
          cols: board.cols,
        };
      }
      currentPlaylist.items.push(item);
    }

    // Update playlist count
    const playlistIndex = playlists.findIndex((p) => p.id === currentPlaylist!.id);
    if (playlistIndex !== -1) {
      playlists[playlistIndex].itemCount = currentPlaylist.items.length;
    }

    renderPlaylistItems();
    renderPlaylists();
    showAddBoardModal(false);
    toast.success(`Added ${selectedBoardsForPlaylist.size} ${selectedBoardsForPlaylist.size === 1 ? 'board' : 'boards'}`);
  } catch (error: any) {
    console.error('Failed to add boards:', error);
    if (error.message?.includes('MAX_ITEMS_REACHED')) {
      toast.error('Playlist is full (max 50 boards)');
    } else {
      toast.error('Failed to add boards');
    }
  } finally {
    confirmAddBoardBtn.classList.remove('loading');
  }
}

// Render subscription view
// Current billing cycle selection
let currentBillingCycle: 'monthly' | 'annual' = 'monthly';

function renderSubscriptionView() {
  if (!currentUser) return;

  const tier = currentUser.subscriptionTier || 'free';
  const tierBadge = document.getElementById('current-tier-badge')!;
  const statusText = document.getElementById('plan-status-text')!;
  const manageBtn = document.getElementById('manage-subscription-btn')!;

  // Set initial billing cycle to user's current cycle, or default to monthly
  currentBillingCycle = currentUser.billingCycle || 'monthly';

  // Update current tier badge
  tierBadge.textContent = tier.charAt(0).toUpperCase() + tier.slice(1);
  tierBadge.setAttribute('data-tier', tier);

  // Update status text
  if (tier === 'free') {
    statusText.textContent = 'You are currently on the free plan. Upgrade to unlock more features!';
    manageBtn.classList.add('hidden');
  } else if (currentUser.subscriptionPeriodEnd) {
    const renewDate = new Date(currentUser.subscriptionPeriodEnd * 1000).toLocaleDateString();
    const billingText = currentUser.billingCycle === 'annual' ? 'annual' : 'monthly';
    statusText.textContent = `Your ${billingText} subscription renews on ${renewDate}.`;
    manageBtn.classList.remove('hidden');
  } else {
    statusText.textContent = `You are on the ${tier} plan.`;
    manageBtn.classList.remove('hidden');
  }

  // Setup billing cycle toggle (remove old listeners first)
  const monthlyBtn = document.getElementById('billing-monthly-btn')!;
  const annualBtn = document.getElementById('billing-annual-btn')!;

  const newMonthlyBtn = monthlyBtn.cloneNode(true) as HTMLButtonElement;
  const newAnnualBtn = annualBtn.cloneNode(true) as HTMLButtonElement;
  monthlyBtn.replaceWith(newMonthlyBtn);
  annualBtn.replaceWith(newAnnualBtn);

  // Set initial active state based on current billing cycle
  if (currentBillingCycle === 'annual') {
    newMonthlyBtn.classList.remove('active');
    newAnnualBtn.classList.add('active');
  } else {
    newMonthlyBtn.classList.add('active');
    newAnnualBtn.classList.remove('active');
  }

  newMonthlyBtn.addEventListener('click', () => {
    currentBillingCycle = 'monthly';
    newMonthlyBtn.classList.add('active');
    newAnnualBtn.classList.remove('active');
    updatePricing();
    updateButtonStates();
  });

  newAnnualBtn.addEventListener('click', () => {
    currentBillingCycle = 'annual';
    newMonthlyBtn.classList.remove('active');
    newAnnualBtn.classList.add('active');
    updatePricing();
    updateButtonStates();
  });

  // Setup Manage Subscription button (remove old listeners first)
  const newManageBtn = manageBtn.cloneNode(true) as HTMLButtonElement;
  manageBtn.replaceWith(newManageBtn);
  newManageBtn.addEventListener('click', async () => {
    try {
      const token = getStoredToken();
      const { portalUrl } = await createPortalSession(token);
      window.location.href = portalUrl;
    } catch (error) {
      console.error('Portal error:', error);
      toast.error('Failed to open subscription management');
    }
  });

  // Add click handlers for upgrade/downgrade buttons (will be updated by updateButtonStates)
  document.querySelectorAll('.btn-plan').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const action = btn.getAttribute('data-action');

      // If it's a downgrade, billing cycle switch, or any "manage" action, open Stripe portal
      if (action === 'manage') {
        try {
          const token = getStoredToken();
          const { portalUrl } = await createPortalSession(token);
          window.location.href = portalUrl;
        } catch (error) {
          console.error('Portal error:', error);
          toast.error('Failed to open subscription management');
        }
        return;
      }

      // For upgrades - check if user already has a paid subscription
      const targetTier = btn.getAttribute('data-tier') as 'pro' | 'commercial';
      if (!targetTier) return;

      try {
        const token = getStoredToken();
        const currentTier = currentUser?.subscriptionTier || 'free';

        // If user already has a paid plan, use portal to upgrade (avoids duplicate subscriptions)
        if (currentTier !== 'free' && currentUser?.stripeCustomerId) {
          const { portalUrl } = await createPortalSession(token);
          window.location.href = portalUrl;
        } else {
          // Free tier users can use checkout for initial subscription
          const { checkoutUrl } = await createCheckoutSession(
            targetTier,
            currentBillingCycle,
            token
          );
          window.location.href = checkoutUrl;
        }
      } catch (error) {
        console.error('Checkout error:', error);
        toast.error('Failed to start checkout. Please try again.');
      }
    });
  });

  // Initial pricing and button state update
  updatePricing();
  updateButtonStates();
}

// Update button states based on current tier and billing cycle
function updateButtonStates() {
  if (!currentUser) return;

  const tier = currentUser.subscriptionTier || 'free';
  const userBillingCycle = currentUser.billingCycle;

  const planCards = document.querySelectorAll('.plan-card');
  planCards.forEach((card, index) => {
    const tiers = ['free', 'pro', 'commercial'];
    const cardTier = tiers[index];
    const button = card.querySelector('.btn-plan') as HTMLButtonElement;

    // Check if this is the current plan (same tier AND same billing cycle)
    const isCurrentPlan = cardTier === tier &&
                          (tier === 'free' || userBillingCycle === currentBillingCycle);

    // Check if it's the same tier but different billing cycle
    const isSameTierDifferentCycle = cardTier === tier &&
                                     tier !== 'free' &&
                                     userBillingCycle !== currentBillingCycle;

    if (isCurrentPlan) {
      button.textContent = 'Current Plan';
      button.className = 'btn btn-plan btn-plan-current';
      button.disabled = true;
      button.removeAttribute('data-tier');
      button.removeAttribute('data-action');
    } else if (isSameTierDifferentCycle) {
      const cycleText = currentBillingCycle === 'monthly' ? 'Monthly' : 'Annual';
      button.textContent = `Switch to ${cycleText}`;
      button.className = 'btn btn-primary btn-plan';
      button.disabled = false;
      button.removeAttribute('data-tier');
      button.setAttribute('data-action', 'manage');
    } else if (getTierLevel(cardTier) < getTierLevel(tier)) {
      const tierName = cardTier.charAt(0).toUpperCase() + cardTier.slice(1);
      button.textContent = `Downgrade to ${tierName}`;
      button.className = 'btn btn-secondary btn-plan';
      button.disabled = false;
      button.removeAttribute('data-tier');
      button.setAttribute('data-action', 'manage');
    } else {
      const tierName = cardTier.charAt(0).toUpperCase() + cardTier.slice(1);
      button.textContent = `Upgrade to ${tierName}`;
      button.className = 'btn btn-primary btn-plan';
      button.disabled = false;
      button.setAttribute('data-tier', cardTier);
      button.removeAttribute('data-action');
    }
  });
}

// Update prices based on billing cycle
function updatePricing() {
  const planCards = document.querySelectorAll('.plan-card');
  const prices = {
    monthly: { pro: '£5', commercial: '£50' },
    annual: { pro: '£50', commercial: '£500' },
  };
  const periods = {
    monthly: '/month',
    annual: '/year',
  };

  planCards.forEach((card, index) => {
    if (index === 0) return; // Skip free tier

    const priceAmount = card.querySelector('.price-amount');
    const pricePeriod = card.querySelector('.price-period');

    if (priceAmount && pricePeriod) {
      const tier = index === 1 ? 'pro' : 'commercial';
      priceAmount.textContent = prices[currentBillingCycle][tier];
      pricePeriod.textContent = periods[currentBillingCycle];
    }
  });
}

// Helper to get tier level for comparison
function getTierLevel(tier: string): number {
  const levels: Record<string, number> = { free: 0, pro: 1, commercial: 2 };
  return levels[tier] || 0;
}

// Format date helper
function formatDate(timestamp: number): string {
  // Convert Unix timestamp (seconds) to milliseconds
  const date = new Date(timestamp * 1000);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  return date.toLocaleDateString();
}

// Update viewer count for current board
async function updateViewerCount() {
  if (!currentBoard) return;

  try {
    const token = getStoredToken();
    const count = await getViewerCount(currentBoard.id, token);
    currentBoard.viewers = count;
    boardViewerCountValue.textContent = String(count);
  } catch (error) {
    console.error('Failed to fetch viewer count:', error);
  }
}

// Initialize or update the board editor
function initBoardEditor(rows: number, cols: number) {
  boardEditorContainer.innerHTML = '';
  boardEditor = new BoardEditor(boardEditorContainer, rows, cols);

  if (!colorToolbar) {
    colorToolbar = new ColorToolbar(
      colorToolbarContainer,
      (color) => boardEditor?.setSelectedColor(color),
      (bg) => boardEditor?.setSelectedBg(bg),
      (mode) => boardEditor?.setMode(mode),
      () => boardEditor?.centerSelection(),
      () => boardEditor?.clear()
    );
  }

  boardEditor.setOnColorChange((color) => colorToolbar?.setSelectedColor(color));
  boardEditor.setOnBgChange((bg) => colorToolbar?.setSelectedBg(bg));
  boardEditor.focus();
}

// Open board editor view
async function openBoard(boardId: string) {
  const board = boards.find((b) => b.id === boardId);
  if (!board) return;

  currentBoard = board;
  boardTitle.textContent = board.name || 'Untitled Board';
  boardUrlDisplay.textContent = getDisplayUrl(board.id);
  boardNameSettingInput.value = board.name || '';
  boardRowsInput.value = String(board.rows);
  boardColsInput.value = String(board.cols);
  clearCheckbox.checked = false;

  // Disable board size controls for free users
  const boardSizeGroup = document.querySelector('.setting-group:has(#board-rows)');
  const boardSizeBadge = document.getElementById('board-size-premium-badge');
  const boardSizeHint = document.getElementById('board-size-hint');

  if (currentUser && currentUser.subscriptionTier === 'free') {
    boardSizeGroup?.classList.add('premium-disabled');
    boardSizeBadge?.classList.remove('hidden');
    boardSizeHint?.classList.remove('hidden');
  } else {
    boardSizeGroup?.classList.remove('premium-disabled');
    boardSizeBadge?.classList.add('hidden');
    boardSizeHint?.classList.add('hidden');
  }

  // Set fillScreen toggle state and premium badge
  const fillScreenGroup = document.querySelector('.setting-group:has(#fill-screen-toggle)');
  const fillScreenBadge = document.getElementById('fill-screen-premium-badge');
  const fillScreenHint = document.getElementById('fill-screen-hint');
  const fillScreenEnabled = board.fillScreen ?? false;

  fillScreenToggle.checked = fillScreenEnabled;
  fillScreenToggle.disabled = currentUser?.subscriptionTier === 'free';

  if (currentUser && currentUser.subscriptionTier === 'free') {
    fillScreenGroup?.classList.add('premium-disabled');
    fillScreenBadge?.classList.remove('hidden');
    if (fillScreenHint) fillScreenHint.textContent = 'Fill screen mode is available on Pro and Commercial plans.';
  } else {
    fillScreenGroup?.classList.remove('premium-disabled');
    fillScreenBadge?.classList.add('hidden');
    if (fillScreenHint) fillScreenHint.textContent = 'Remove bezel and scale edge-to-edge for embedding.';
  }

  // Reset sound toggle state
  soundToggle.checked = false;

  initBoardEditor(board.rows, board.cols);
  showView('editor');

  // Fetch current board state
  try {
    const token = getStoredToken();
    const state = await getBoardState(boardId, token);
    boardEditor?.setCells(state.cells);
  } catch (error) {
    console.error('Failed to fetch board state:', error);
  }

  // Clear any existing viewer count interval
  if (viewerCountInterval) {
    clearInterval(viewerCountInterval);
  }

  // Fetch initial viewer count and start polling
  await updateViewerCount();
  viewerCountInterval = window.setInterval(updateViewerCount, 5000); // Update every 5 seconds
}

// Token management
const TOKEN_KEY = 'flipboard_token';

function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

function setStoredToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
}

function clearStoredToken() {
  localStorage.removeItem(TOKEN_KEY);
}

function handleOAuthCallback() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  const error = params.get('error');
  const message = params.get('message');

  if (error === 'access_denied') {
    toast.error(message || 'Access denied to this environment');
    window.history.replaceState({}, '', window.location.pathname);
    return;
  }

  if (token) {
    setStoredToken(token);
    window.history.replaceState({}, '', window.location.pathname);
  }
}

// Admin view
async function loadAdminView() {
  try {
    const token = getStoredToken();

    // Load metrics
    const metrics = await getAdminMetrics(token);
    document.getElementById('admin-users-count')!.textContent = metrics.users.toString();
    document.getElementById('admin-boards-count')!.textContent = metrics.boards.toString();
    document.getElementById('admin-viewers-count')!.textContent = metrics.viewers.toString();

    // Load users
    const { users } = await getAdminUsers(token);
    renderAdminUsers(users);
  } catch (error) {
    console.error('Failed to load admin data:', error);
    toast.error('Failed to load admin data');
  }
}

function renderAdminUsers(users: AdminUser[]) {
  const tbody = document.getElementById('admin-users-list')!;

  if (users.length === 0) {
    tbody.innerHTML = '<tr><td colspan="6" class="empty-row">No users found</td></tr>';
    return;
  }

  tbody.innerHTML = users.map((user) => {
    const createdDate = new Date(user.createdAt * 1000).toLocaleDateString();
    const tierBadge = user.subscriptionTier === 'free'
      ? '<span class="tier-badge tier-free">Free</span>'
      : user.subscriptionTier === 'pro'
      ? '<span class="tier-badge tier-pro">Pro</span>'
      : '<span class="tier-badge tier-commercial">Commercial</span>';

    return `
      <tr>
        <td>${user.email}</td>
        <td>${user.name || '-'}</td>
        <td>${tierBadge}</td>
        <td>${user.boardCount}</td>
        <td>${createdDate}</td>
        <td>
          <button class="btn-text-only view-user-btn" data-user-id="${user.id}">View</button>
        </td>
      </tr>
    `;
  }).join('');

  // Add click handlers for View buttons
  tbody.querySelectorAll('.view-user-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const userId = (btn as HTMLElement).getAttribute('data-user-id')!;
      navigateToAdminUser(userId);
    });
  });
}

// Navigate to admin user detail page
function navigateToAdminUser(userId: string) {
  window.location.hash = `admin/users/${userId}`;
}

// Parse hash route
function parseHash(): { route: string; params: Record<string, string> } {
  const hash = window.location.hash.slice(1); // Remove leading #
  if (!hash) return { route: '', params: {} };

  // Match pattern: admin/users/:userId
  const match = hash.match(/^admin\/users\/([^/]+)$/);
  if (match) {
    return {
      route: 'admin/users/:id',
      params: { userId: match[1] }
    };
  }

  return { route: hash, params: {} };
}

// Load admin user detail view
async function loadAdminUserView(userId: string) {
  try {
    const token = getStoredToken();
    currentAdminUser = await getAdminUserDetail(userId, token);

    // Render user profile
    const user = currentAdminUser.user;
    adminUserAvatar.src = user.avatarUrl || '';
    adminUserName.textContent = user.name || user.email;
    adminUserEmail.textContent = user.email;
    adminUserCreated.textContent = `Joined ${new Date(user.createdAt * 1000).toLocaleDateString()}`;

    // Render subscription details
    adminUserTier.textContent = user.subscriptionTier.charAt(0).toUpperCase() + user.subscriptionTier.slice(1);
    adminUserTier.setAttribute('data-tier', user.subscriptionTier);
    adminUserTier.className = `tier-badge tier-${user.subscriptionTier}`;
    adminUserStatus.textContent = user.subscriptionStatus.charAt(0).toUpperCase() + user.subscriptionStatus.slice(1);
    adminUserBillingCycle.textContent = user.billingCycle ? user.billingCycle.charAt(0).toUpperCase() + user.billingCycle.slice(1) : '-';
    adminUserPeriodEnd.textContent = user.subscriptionPeriodEnd
      ? new Date(user.subscriptionPeriodEnd * 1000).toLocaleDateString()
      : '-';
    adminUserStripeId.textContent = user.stripeCustomerId || '-';

    // Render boards
    adminUserBoardsCount.textContent = currentAdminUser.boards.length.toString();
    if (currentAdminUser.boards.length === 0) {
      adminUserBoardsList.innerHTML = '';
      adminUserBoardsEmpty.classList.remove('hidden');
    } else {
      adminUserBoardsEmpty.classList.add('hidden');
      adminUserBoardsList.innerHTML = currentAdminUser.boards.map(board => `
        <div class="admin-board-card">
          <div class="admin-board-info">
            <h3>${board.name || 'Untitled Board'}</h3>
            <div class="admin-board-meta">
              <span>${board.rows}×${board.cols}</span>
              <span>Created ${new Date(board.createdAt * 1000).toLocaleDateString()}</span>
            </div>
          </div>
          <button class="btn-icon-only delete-user-board-btn" data-board-id="${board.id}" title="Delete board">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14z"/>
            </svg>
          </button>
        </div>
      `).join('');

      // Add delete board handlers
      adminUserBoardsList.querySelectorAll('.delete-user-board-btn').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const boardId = (btn as HTMLElement).getAttribute('data-board-id')!;
          await handleDeleteUserBoard(userId, boardId);
        });
      });
    }

    // Render API keys
    adminUserApikeysCount.textContent = currentAdminUser.apiKeys.length.toString();
    if (currentAdminUser.apiKeys.length === 0) {
      adminUserApikeysList.innerHTML = '';
      adminUserApikeysEmpty.classList.remove('hidden');
    } else {
      adminUserApikeysEmpty.classList.add('hidden');
      adminUserApikeysList.innerHTML = currentAdminUser.apiKeys.map(key => `
        <div class="admin-apikey-card">
          <div class="admin-apikey-info">
            <h3>${key.name}</h3>
            <div class="admin-apikey-meta">
              <code>${key.keyPrefix}...</code>
              ${key.scopes.map(scope => `<span class="scope-badge ${scope}">${scope}</span>`).join('')}
              <span>${key.lastUsedAt ? `Last used ${formatDate(key.lastUsedAt)}` : 'Never used'}</span>
            </div>
          </div>
          <button class="btn-icon-only delete-user-apikey-btn" data-key-id="${key.id}" title="Delete API key">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14z"/>
            </svg>
          </button>
        </div>
      `).join('');

      // Add delete API key handlers
      adminUserApikeysList.querySelectorAll('.delete-user-apikey-btn').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const keyId = (btn as HTMLElement).getAttribute('data-key-id')!;
          await handleDeleteUserApiKey(userId, keyId);
        });
      });
    }

    showView('admin-user');
  } catch (error) {
    console.error('Failed to load user details:', error);
    toast.error('Failed to load user details');
    // Navigate back to admin list on error
    window.location.hash = '';
    showView('admin');
  }
}

// Handle hash changes for routing
function handleHashChange() {
  const { route, params } = parseHash();

  if (route === 'admin/users/:id' && params.userId) {
    loadAdminUserView(params.userId);
  } else if (window.location.hash === '') {
    // No hash - this will be handled by regular view logic
  }
}

// Admin user detail handlers
async function handleDeleteUserBoard(userId: string, boardId: string) {
  if (!currentAdminUser) return;

  const board = currentAdminUser.boards.find(b => b.id === boardId);
  if (!board) return;

  if (!confirm(`Are you sure you want to delete board "${board.name || board.id}"? This action cannot be undone.`)) {
    return;
  }

  try {
    const token = getStoredToken();
    await deleteAdminUserBoard(userId, boardId, token);
    toast.success('Board deleted successfully');
    // Reload user details
    await loadAdminUserView(userId);
  } catch (error) {
    console.error('Failed to delete board:', error);
    toast.error('Failed to delete board');
  }
}

async function handleDeleteUserApiKey(userId: string, keyId: string) {
  if (!currentAdminUser) return;

  const apiKey = currentAdminUser.apiKeys.find(k => k.id === keyId);
  if (!apiKey) return;

  if (!confirm(`Are you sure you want to delete API key "${apiKey.name}"? This action cannot be undone.`)) {
    return;
  }

  try {
    const token = getStoredToken();
    await deleteAdminUserApiKey(userId, keyId, token);
    toast.success('API key deleted successfully');
    // Reload user details
    await loadAdminUserView(userId);
  } catch (error) {
    console.error('Failed to delete API key:', error);
    toast.error('Failed to delete API key');
  }
}

async function handleImpersonate() {
  if (!currentAdminUser) return;

  const user = currentAdminUser.user;
  if (!confirm(`Impersonate ${user.name || user.email}? You'll be logged in as this user for 1 hour.`)) {
    return;
  }

  try {
    const token = getStoredToken();

    // Store admin token in sessionStorage
    sessionStorage.setItem('admin_token', token || '');

    // Get impersonation token
    const result = await impersonateUser(user.id, token);

    // Set the impersonation token
    setStoredToken(result.token);

    // Show impersonation banner
    impersonationUserName.textContent = user.name || user.email;
    impersonationBanner.classList.remove('hidden');

    // Reload app as impersonated user
    window.location.href = '/';
  } catch (error) {
    console.error('Failed to impersonate user:', error);
    toast.error('Failed to impersonate user');
  }
}

async function handleDeleteUser(userId: string) {
  if (!currentAdminUser) return;

  const user = currentAdminUser.user;
  if (!confirm(`Are you sure you want to DELETE user ${user.email}? This will delete all their boards, API keys, and sessions. This action CANNOT be undone.`)) {
    return;
  }

  if (!confirm(`Final confirmation: Delete ${user.email} and ALL associated data?`)) {
    return;
  }

  try {
    const token = getStoredToken();
    await deleteAdminUser(userId, token);
    toast.success('User deleted successfully');
    // Navigate back to admin list
    window.location.hash = '';
    showView('admin');
    // Reload admin list
    loadAdminView();
  } catch (error) {
    console.error('Failed to delete user:', error);
    toast.error('Failed to delete user');
  }
}

function showEditSubscriptionModal() {
  if (!currentAdminUser) return;

  const user = currentAdminUser.user;
  editTierSelect.value = user.subscriptionTier;
  editStatusSelect.value = user.subscriptionStatus;

  // Convert timestamp to datetime-local format
  if (user.subscriptionPeriodEnd) {
    const date = new Date(user.subscriptionPeriodEnd * 1000);
    const offset = date.getTimezoneOffset() * 60000;
    const localDate = new Date(date.getTime() - offset);
    editPeriodEndInput.value = localDate.toISOString().slice(0, 16);
  } else {
    editPeriodEndInput.value = '';
  }

  editSubscriptionModal.classList.remove('hidden');
}

async function handleSaveSubscription() {
  if (!currentAdminUser) return;

  const tier = editTierSelect.value as 'free' | 'pro' | 'commercial';
  const status = editStatusSelect.value as 'active' | 'past_due' | 'cancelled' | 'trialing';
  const periodEndValue = editPeriodEndInput.value;
  const periodEnd = periodEndValue ? Math.floor(new Date(periodEndValue).getTime() / 1000) : null;

  try {
    const token = getStoredToken();
    await updateAdminUserSubscription(
      currentAdminUser.user.id,
      {
        subscriptionTier: tier,
        subscriptionStatus: status,
        subscriptionPeriodEnd: periodEnd,
      },
      token
    );

    toast.success('Subscription updated successfully');
    editSubscriptionModal.classList.add('hidden');

    // Reload user details
    await loadAdminUserView(currentAdminUser.user.id);
  } catch (error) {
    console.error('Failed to update subscription:', error);
    toast.error('Failed to update subscription');
  }
}

function handleEndImpersonation() {
  const adminToken = sessionStorage.getItem('admin_token');
  if (!adminToken) {
    toast.error('Admin token not found');
    return;
  }

  // Restore admin token
  setStoredToken(adminToken);
  sessionStorage.removeItem('admin_token');

  // Hide banner
  impersonationBanner.classList.add('hidden');

  // Reload app as admin
  window.location.href = '/';
}

// Initialize
async function init() {
  // Show staging banner if on staging environment
  const stagingBanner = document.getElementById('staging-banner');
  if (stagingBanner && window.location.hostname.includes('staging')) {
    stagingBanner.classList.remove('hidden');
  }

  handleOAuthCallback();

  try {
    const token = getStoredToken();
    if (token) {
      currentUser = await getCurrentUser(token);
    }

    if (currentUser) {
      userAvatar.src = currentUser.avatarUrl || '';
      userName.textContent = currentUser.name || currentUser.email;
      boards = await getBoards(token!);
      renderBoards();

      // Show admin nav item if user is admin
      const adminNavItem = document.getElementById('admin-nav-item');
      if (adminNavItem && currentUser.isAdmin) {
        adminNavItem.classList.remove('hidden');
      }

      // Check for view parameter in URL
      const urlParams = new URLSearchParams(window.location.search);
      const viewParam = urlParams.get('view');

      if (viewParam === 'subscription') {
        renderSubscriptionView();
        showView('subscription');
      } else if (viewParam === 'playlists') {
        playlists = await getPlaylists(token!);
        renderPlaylists();
        showView('playlists');
      } else if (viewParam === 'apikeys') {
        await renderApiKeys();
        showView('apikeys');
      } else if (viewParam === 'admin' && currentUser.isAdmin) {
        showView('admin');
      } else {
        showView('boards');
      }

      // Check for success message
      if (urlParams.get('success') === 'true') {
        toast.success('Subscription successfully updated!');
      }

      // Clean URL parameters
      if (viewParam || urlParams.get('success')) {
        const cleanUrl = viewParam
          ? `${window.location.pathname}`
          : window.location.pathname;
        window.history.replaceState({}, '', cleanUrl);
      }
    } else {
      clearStoredToken();
      showView('login');
    }
  } catch (error) {
    console.error('Init error:', error);
    clearStoredToken();
    showView('login');
  } finally {
    // Remove loading state once we've determined which view to show
    document.getElementById('app')?.classList.remove('app-loading');
  }
}

// Event handlers

// Login
loginBtn.addEventListener('click', () => {
  window.location.href = getGoogleLoginUrl();
});

// User menu
userMenuBtn.addEventListener('click', (e) => {
  e.stopPropagation();
  toggleUserMenu();
});

document.addEventListener('click', () => {
  closeUserMenu();
});

logoutBtn.addEventListener('click', async () => {
  const token = getStoredToken();
  await logout(token);
  clearStoredToken();
  currentUser = null;
  closeUserMenu();
  showView('login');
});

// Mobile sidebar
mobileMenuBtn.addEventListener('click', () => {
  openMobileSidebar();
});

sidebarBackdrop.addEventListener('click', () => {
  closeMobileSidebar();
});

// Create board
createBoardBtn.addEventListener('click', () => {
  // Check if free user is at board limit
  if (currentUser?.subscriptionTier === 'free' && boards.length >= 1) {
    toast.error('Free plan allows 1 board. Upgrade to Pro to create more boards.');
    return;
  }
  showModal(true);
});

emptyCreateBtn?.addEventListener('click', () => {
  // Check if free user is at board limit
  if (currentUser?.subscriptionTier === 'free' && boards.length >= 1) {
    toast.error('Free plan allows 1 board. Upgrade to Pro to create more boards.');
    return;
  }
  showModal(true);
});

cancelCreateBtn.addEventListener('click', () => showModal(false));

confirmCreateBtn.addEventListener('click', async () => {
  confirmCreateBtn.classList.add('loading');
  try {
    const name = boardNameInput.value.trim() || undefined;
    const rows = parseInt(createBoardRowsInput.value, 10) || 6;
    const cols = parseInt(createBoardColsInput.value, 10) || 22;
    const token = getStoredToken();

    const board = await createBoard(name, rows, cols, token);
    boards.unshift(board);
    renderBoards();
    showModal(false);
  } catch (error: any) {
    console.error('Create board error:', error);

    // Parse error response for friendly messages
    if (error.message?.includes('BOARD_LIMIT_REACHED') || error.message?.includes('Board limit reached')) {
      toast.error('Board limit reached. Upgrade to Pro to create more boards.');
    } else if (error.message?.includes('CUSTOM_SIZE_REQUIRES_UPGRADE')) {
      toast.error('Custom board sizes require a Pro or Commercial plan.');
    } else {
      toast.error('Failed to create board');
    }
  } finally {
    confirmCreateBtn.classList.remove('loading');
  }
});

createModal.addEventListener('click', (e) => {
  if (e.target === createModal) showModal(false);
});

// Board editor
backBtn.addEventListener('click', () => {
  // Clear viewer count polling
  if (viewerCountInterval) {
    clearInterval(viewerCountInterval);
    viewerCountInterval = null;
  }
  currentBoard = null;
  showView('boards');
});

openDisplayBtn.addEventListener('click', () => {
  if (currentBoard) {
    window.open(getDisplayUrl(currentBoard.id), '_blank');
  }
});

sendMessageBtn.addEventListener('click', async () => {
  if (!currentBoard || !boardEditor) return;
  const text = boardEditor.toMarkupText();
  if (!text.trim()) return;

  sendMessageBtn.classList.add('loading');
  try {
    const token = getStoredToken();
    await sendMessage(currentBoard.id, text, clearCheckbox.checked, token);
  } catch (error) {
    console.error('Send message error:', error);
    toast.error('Failed to send message');
  } finally {
    sendMessageBtn.classList.remove('loading');
  }
});

// Settings drawer
settingsBtn.addEventListener('click', () => openSettings());
closeSettingsBtn.addEventListener('click', () => closeSettings());
settingsBackdrop.addEventListener('click', () => closeSettings());

// Settings actions
copyUrlBtn.addEventListener('click', async () => {
  if (!currentBoard) return;
  const url = getDisplayUrl(currentBoard.id);
  try {
    await navigator.clipboard.writeText(url);
    copyUrlBtn.classList.add('copied');
    setTimeout(() => copyUrlBtn.classList.remove('copied'), 2000);
  } catch (error) {
    console.error('Failed to copy URL:', error);
  }
});

qrUrlBtn.addEventListener('click', async () => {
  if (!currentBoard) return;
  const url = getDisplayUrl(currentBoard.id);
  qrUrlText.textContent = url;
  await drawQRCode(qrCanvas, url);
  qrModal.classList.remove('hidden');
});

closeQrBtn.addEventListener('click', () => qrModal.classList.add('hidden'));
qrModal.addEventListener('click', (e) => {
  if (e.target === qrModal) qrModal.classList.add('hidden');
});

updateNameBtn.addEventListener('click', async () => {
  if (!currentBoard) return;
  const name = boardNameSettingInput.value.trim() || null;
  if (name === currentBoard.name) return;

  updateNameBtn.classList.add('loading');
  try {
    const token = getStoredToken();
    const updated = await updateBoard(currentBoard.id, { name: name || undefined }, token);
    currentBoard.name = updated.name;
    boardTitle.textContent = updated.name || 'Untitled Board';
    const idx = boards.findIndex((b) => b.id === currentBoard!.id);
    if (idx !== -1) boards[idx] = { ...boards[idx], name: updated.name };
    renderBoards();
  } catch (error) {
    console.error('Update name error:', error);
    toast.error('Failed to update board name');
    boardNameSettingInput.value = currentBoard.name || '';
  } finally {
    updateNameBtn.classList.remove('loading');
  }
});

updateSizeBtn.addEventListener('click', async () => {
  if (!currentBoard) return;
  const rows = parseInt(boardRowsInput.value, 10);
  const cols = parseInt(boardColsInput.value, 10);
  if (rows === currentBoard.rows && cols === currentBoard.cols) return;

  updateSizeBtn.classList.add('loading');
  try {
    const token = getStoredToken();
    const updated = await updateBoard(currentBoard.id, { rows, cols }, token);
    currentBoard.rows = updated.rows;
    currentBoard.cols = updated.cols;
    const idx = boards.findIndex((b) => b.id === currentBoard!.id);
    if (idx !== -1) boards[idx] = { ...boards[idx], rows: updated.rows, cols: updated.cols };
    boardEditor?.setSize(updated.rows, updated.cols);
    renderBoards();
  } catch (error: any) {
    console.error('Update size error:', error);

    // Parse error response for friendly messages
    if (error.message?.includes('CUSTOM_SIZE_REQUIRES_UPGRADE')) {
      toast.error('Custom board sizes require a Pro or Commercial plan.');
    } else {
      toast.error('Failed to update board size');
    }

    boardRowsInput.value = String(currentBoard.rows);
    boardColsInput.value = String(currentBoard.cols);
  } finally {
    updateSizeBtn.classList.remove('loading');
  }
});

fillScreenToggle.addEventListener('change', async () => {
  if (!currentBoard) return;
  const newState = fillScreenToggle.checked;

  try {
    const token = getStoredToken();
    fillScreenToggle.disabled = true;
    const updated = await updateBoard(currentBoard.id, { fillScreen: newState }, token);
    currentBoard.fillScreen = updated.fillScreen;
    const idx = boards.findIndex((b) => b.id === currentBoard!.id);
    if (idx !== -1) boards[idx] = { ...boards[idx], fillScreen: updated.fillScreen };
    toast.success(`Fill screen ${newState ? 'enabled' : 'disabled'}`);
  } catch (error) {
    console.error('Toggle fill screen error:', error);
    toast.error('Failed to update fill screen setting');
    // Revert checkbox on error
    fillScreenToggle.checked = !newState;
  } finally {
    fillScreenToggle.disabled = currentUser?.subscriptionTier === 'free';
  }
});

soundToggle.addEventListener('change', async () => {
  if (!currentBoard) return;
  const newState = soundToggle.checked;

  try {
    const token = getStoredToken();
    soundToggle.disabled = true;
    await toggleSound(currentBoard.id, newState, token);
  } catch (error) {
    console.error('Toggle sound error:', error);
    // Revert checkbox on error
    soundToggle.checked = !newState;
  } finally {
    soundToggle.disabled = false;
  }
});

deleteBoardBtn.addEventListener('click', async () => {
  if (!currentBoard) return;
  if (!confirm(`Are you sure you want to delete "${currentBoard.name || currentBoard.id}"?`)) return;

  deleteBoardBtn.classList.add('loading');
  try {
    const token = getStoredToken();
    await deleteBoard(currentBoard.id, token);
    boards = boards.filter((b) => b.id !== currentBoard!.id);
    currentBoard = null;
    renderBoards();
    closeSettings();
    showView('boards');
  } catch (error) {
    console.error('Delete board error:', error);
    toast.error('Failed to delete board');
  } finally {
    deleteBoardBtn.classList.remove('loading');
  }
});

// Sidebar navigation
document.querySelectorAll('.nav-item').forEach((item) => {
  item.addEventListener('click', async (e) => {
    e.preventDefault();
    const navType = item.getAttribute('data-nav');

    if (navType === 'boards') {
      showView('boards');
    } else if (navType === 'playlists') {
      try {
        const token = getStoredToken();
        playlists = await getPlaylists(token);
        renderPlaylists();
        showView('playlists');
      } catch (error) {
        console.error('Failed to load playlists:', error);
        toast.error('Failed to load playlists');
      }
    } else if (navType === 'api-keys') {
      try {
        const token = getStoredToken();
        apiKeys = await getApiKeys(token);
        renderApiKeys();
        showView('apikeys');
      } catch (error) {
        console.error('Failed to load API keys:', error);
        toast.error('Failed to load API keys');
      }
    } else if (navType === 'subscription') {
      renderSubscriptionView();
      showView('subscription');
    } else if (navType === 'admin') {
      showView('admin');
    }
  });
});

// API Keys - Create modal
function showCreateApiKeyModal(show: boolean) {
  createApiKeyModal.classList.toggle('hidden', !show);
  if (show) {
    apiKeyNameInput.value = '';
    scopeReadCheckbox.checked = true;
    scopeWriteCheckbox.checked = false;
  }
}

createApiKeyBtn.addEventListener('click', () => showCreateApiKeyModal(true));
emptyCreateApiKeyBtn?.addEventListener('click', () => showCreateApiKeyModal(true));
cancelCreateApiKeyBtn.addEventListener('click', () => showCreateApiKeyModal(false));

createApiKeyModal.addEventListener('click', (e) => {
  if (e.target === createApiKeyModal) showCreateApiKeyModal(false);
});

confirmCreateApiKeyBtn.addEventListener('click', async () => {
  const name = apiKeyNameInput.value.trim();
  if (!name) {
    toast.info('Please enter a name for the API key');
    return;
  }

  const scopes: string[] = [];
  if (scopeReadCheckbox.checked) scopes.push('read');
  if (scopeWriteCheckbox.checked) scopes.push('write');

  if (scopes.length === 0) {
    toast.info('Please select at least one scope');
    return;
  }

  confirmCreateApiKeyBtn.classList.add('loading');
  try {
    const token = getStoredToken();
    const response = await createApiKey(name, scopes, token);

    // Add to list without the full key
    const { key, ...keyWithoutSecret } = response.apiKey;
    apiKeys.unshift(keyWithoutSecret);
    renderApiKeys();

    // Show the full key once
    apiKeyDisplay.textContent = key;
    showCreateApiKeyModal(false);
    apiKeyCreatedModal.classList.remove('hidden');
  } catch (error) {
    console.error('Create API key error:', error);
    toast.error('Failed to create API key');
  } finally {
    confirmCreateApiKeyBtn.classList.remove('loading');
  }
});

// API Key Created modal - Copy and close
copyApiKeyBtn.addEventListener('click', async () => {
  const key = apiKeyDisplay.textContent;
  if (!key) return;

  try {
    await navigator.clipboard.writeText(key);
    const originalText = copyApiKeyBtn.textContent;
    copyApiKeyBtn.textContent = 'Copied!';
    copyApiKeyBtn.classList.add('copied');
    setTimeout(() => {
      copyApiKeyBtn.textContent = originalText;
      copyApiKeyBtn.classList.remove('copied');
    }, 2000);
  } catch (error) {
    console.error('Failed to copy API key:', error);
    toast.error('Failed to copy to clipboard');
  }
});

closeApiKeyCreatedBtn.addEventListener('click', () => {
  apiKeyCreatedModal.classList.add('hidden');
  apiKeyDisplay.textContent = '';
});

apiKeyCreatedModal.addEventListener('click', (e) => {
  if (e.target === apiKeyCreatedModal) {
    apiKeyCreatedModal.classList.add('hidden');
    apiKeyDisplay.textContent = '';
  }
});

// Playlists - Create
createPlaylistBtn.addEventListener('click', async () => {
  // Check tier limits
  if (currentUser?.subscriptionTier === 'free') {
    toast.error('Playlists are not available on the free plan. Upgrade to Pro or Commercial.');
    return;
  }

  const tierLimits: Record<string, number> = { free: 0, pro: 3, commercial: 10 };
  const maxPlaylists = tierLimits[currentUser?.subscriptionTier || 'free'];
  if (playlists.length >= maxPlaylists) {
    toast.error(`Playlist limit reached. ${currentUser?.subscriptionTier === 'pro' ? 'Upgrade to Commercial for more playlists.' : 'You have reached your playlist limit.'}`);
    return;
  }

  createPlaylistBtn.classList.add('loading');
  try {
    const token = getStoredToken();
    const playlist = await createPlaylist(undefined, 10, false, token);
    playlists.unshift(playlist);
    renderPlaylists();
    toast.success('Playlist created');
    openPlaylist(playlist.id);
  } catch (error: any) {
    console.error('Create playlist error:', error);
    if (error.message?.includes('PLAYLIST_LIMIT_REACHED')) {
      toast.error('Playlist limit reached. Upgrade for more playlists.');
    } else {
      toast.error('Failed to create playlist');
    }
  } finally {
    createPlaylistBtn.classList.remove('loading');
  }
});

emptyCreatePlaylistBtn?.addEventListener('click', async () => {
  // Check tier limits
  if (currentUser?.subscriptionTier === 'free') {
    toast.error('Playlists are not available on the free plan. Upgrade to Pro or Commercial.');
    return;
  }

  emptyCreatePlaylistBtn.classList.add('loading');
  try {
    const token = getStoredToken();
    const playlist = await createPlaylist(undefined, 10, false, token);
    playlists.unshift(playlist);
    renderPlaylists();
    toast.success('Playlist created');
    openPlaylist(playlist.id);
  } catch (error: any) {
    console.error('Create playlist error:', error);
    if (error.message?.includes('PLAYLIST_LIMIT_REACHED')) {
      toast.error('Playlist limit reached. Upgrade for more playlists.');
    } else {
      toast.error('Failed to create playlist');
    }
  } finally {
    emptyCreatePlaylistBtn.classList.remove('loading');
  }
});

// Playlist editor
playlistBackBtn.addEventListener('click', () => {
  currentPlaylist = null;
  showView('playlists');
});

openPlaylistDisplayBtn.addEventListener('click', () => {
  if (currentPlaylist) {
    window.open(getPlaylistDisplayUrl(currentPlaylist.id), '_blank');
  }
});

playlistSettingsBtn.addEventListener('click', () => openPlaylistSettings());
closePlaylistSettingsBtn.addEventListener('click', () => closePlaylistSettings());
playlistSettingsBackdrop.addEventListener('click', () => closePlaylistSettings());

// Playlist settings - Update name
updatePlaylistNameBtn.addEventListener('click', async () => {
  if (!currentPlaylist) return;
  const name = playlistNameInput.value.trim() || null;
  if (name === currentPlaylist.name) return;

  updatePlaylistNameBtn.classList.add('loading');
  try {
    const token = getStoredToken();
    await updatePlaylist(currentPlaylist.id, { name: name || undefined }, token);
    currentPlaylist.name = name;
    playlistTitle.textContent = name || 'Untitled Playlist';
    const idx = playlists.findIndex((p) => p.id === currentPlaylist!.id);
    if (idx !== -1) playlists[idx] = { ...playlists[idx], name };
    renderPlaylists();
    toast.success('Playlist name updated');
  } catch (error) {
    console.error('Update name error:', error);
    toast.error('Failed to update playlist name');
    playlistNameInput.value = currentPlaylist.name || '';
  } finally {
    updatePlaylistNameBtn.classList.remove('loading');
  }
});

// Playlist settings - Update size
updatePlaylistSizeBtn.addEventListener('click', async () => {
  if (!currentPlaylist) return;

  // Allow empty inputs to clear size (use board's size)
  if (playlistRowsInput.value === '' && playlistColsInput.value === '') {
    if (currentPlaylist.rows === null && currentPlaylist.cols === null) return;

    updatePlaylistSizeBtn.classList.add('loading');
    try {
      const token = getStoredToken();
      await updatePlaylist(currentPlaylist.id, { rows: null, cols: null }, token);
      currentPlaylist.rows = null;
      currentPlaylist.cols = null;
      const idx = playlists.findIndex((p) => p.id === currentPlaylist!.id);
      if (idx !== -1) playlists[idx] = { ...playlists[idx], rows: null, cols: null };
      toast.success('Playlist will use each board\'s size');
    } catch (error) {
      console.error('Clear size error:', error);
      toast.error('Failed to clear playlist size');
    } finally {
      updatePlaylistSizeBtn.classList.remove('loading');
    }
    return;
  }

  const rows = parseInt(playlistRowsInput.value, 10);
  const cols = parseInt(playlistColsInput.value, 10);

  if (isNaN(rows) || isNaN(cols) || rows < 1 || rows > 12 || cols < 1 || cols > 40) {
    toast.error('Invalid size. Rows: 1-12, Cols: 1-40 (or leave empty for default)');
    return;
  }

  if (rows === currentPlaylist.rows && cols === currentPlaylist.cols) return;

  updatePlaylistSizeBtn.classList.add('loading');
  try {
    const token = getStoredToken();
    await updatePlaylist(currentPlaylist.id, { rows, cols }, token);
    currentPlaylist.rows = rows;
    currentPlaylist.cols = cols;
    const idx = playlists.findIndex((p) => p.id === currentPlaylist!.id);
    if (idx !== -1) playlists[idx] = { ...playlists[idx], rows, cols };
    toast.success('Playlist size updated');
  } catch (error) {
    console.error('Update size error:', error);
    toast.error('Failed to update playlist size');
  } finally {
    updatePlaylistSizeBtn.classList.remove('loading');
  }
});

// Playlist settings - Clear size (use board's size)
// Button doesn't exist in HTML - users clear by emptying the input fields
// clearPlaylistSizeBtn.addEventListener('click', async () => {
//   if (!currentPlaylist) return;
//   if (currentPlaylist.rows === null && currentPlaylist.cols === null) return;
//
//   clearPlaylistSizeBtn.classList.add('loading');
//   try {
//     const token = getStoredToken();
//     await updatePlaylist(currentPlaylist.id, { rows: null, cols: null }, token);
//     currentPlaylist.rows = null;
//     currentPlaylist.cols = null;
//     const idx = playlists.findIndex((p) => p.id === currentPlaylist!.id);
//     if (idx !== -1) playlists[idx] = { ...playlists[idx], rows: null, cols: null };
//     playlistRowsInput.value = '';
//     playlistColsInput.value = '';
//     toast.success('Playlist will use each board\'s size');
//   } catch (error) {
//     console.error('Clear size error:', error);
//     toast.error('Failed to clear playlist size');
//   } finally {
//     clearPlaylistSizeBtn.classList.remove('loading');
//   }
// });

// Playlist settings - Change fill screen
playlistFillScreenSelect.addEventListener('change', async () => {
  if (!currentPlaylist) return;
  const value = playlistFillScreenSelect.value;
  const newState = value === '' ? null : value === 'true';

  try {
    const token = getStoredToken();
    playlistFillScreenSelect.disabled = true;
    await updatePlaylist(currentPlaylist.id, { fillScreen: newState }, token);
    currentPlaylist.fillScreen = newState;
    const idx = playlists.findIndex((p) => p.id === currentPlaylist!.id);
    if (idx !== -1) playlists[idx] = { ...playlists[idx], fillScreen: newState };
    if (newState === null) {
      toast.success('Playlist will use each board\'s fill screen setting');
    } else {
      toast.success(`Fill screen ${newState ? 'enabled' : 'disabled'}`);
    }
  } catch (error) {
    console.error('Change fill screen error:', error);
    toast.error('Failed to update fill screen setting');
    playlistFillScreenSelect.value = currentPlaylist.fillScreen === null ? '' : String(currentPlaylist.fillScreen);
  } finally {
    playlistFillScreenSelect.disabled = false;
  }
});

// Playlist settings - Change sound
playlistSoundSelect.addEventListener('change', async () => {
  if (!currentPlaylist) return;
  const value = playlistSoundSelect.value;
  const newState = value === '' ? null : value === 'true';

  try {
    const token = getStoredToken();
    playlistSoundSelect.disabled = true;
    await updatePlaylist(currentPlaylist.id, { soundEnabled: newState }, token);
    currentPlaylist.soundEnabled = newState;
    const idx = playlists.findIndex((p) => p.id === currentPlaylist!.id);
    if (idx !== -1) playlists[idx] = { ...playlists[idx], soundEnabled: newState };
    if (newState === null) {
      toast.success('Playlist will use each board\'s sound setting');
    } else {
      toast.success(`Sound ${newState ? 'enabled' : 'disabled'}`);
    }
  } catch (error) {
    console.error('Change sound error:', error);
    toast.error('Failed to update sound setting');
    playlistSoundSelect.value = currentPlaylist.soundEnabled === null ? '' : String(currentPlaylist.soundEnabled);
  } finally {
    playlistSoundSelect.disabled = false;
  }
});

// Playlist settings - Update default duration
updatePlaylistDurationBtn.addEventListener('click', async () => {
  if (!currentPlaylist) return;
  const duration = parseInt(playlistDefaultDurationInput.value, 10);

  if (isNaN(duration) || duration < 5 || duration > 3600) {
    toast.error('Duration must be between 5 and 3600 seconds');
    return;
  }

  if (duration === currentPlaylist.defaultDuration) return;

  updatePlaylistDurationBtn.classList.add('loading');
  try {
    const token = getStoredToken();
    await updatePlaylist(currentPlaylist.id, { defaultDuration: duration }, token);
    currentPlaylist.defaultDuration = duration;
    const idx = playlists.findIndex((p) => p.id === currentPlaylist!.id);
    if (idx !== -1) playlists[idx] = { ...playlists[idx], defaultDuration: duration };
    toast.success('Default duration updated');
    // Re-render to update displayed durations
    renderPlaylistItems();
  } catch (error) {
    console.error('Update duration error:', error);
    toast.error('Failed to update default duration');
  } finally {
    updatePlaylistDurationBtn.classList.remove('loading');
  }
});

// Playlist settings - Toggle shuffle
playlistShuffleToggle.addEventListener('change', async () => {
  if (!currentPlaylist) return;
  const newState = playlistShuffleToggle.checked;

  try {
    const token = getStoredToken();
    playlistShuffleToggle.disabled = true;
    await updatePlaylist(currentPlaylist.id, { shuffle: newState }, token);
    currentPlaylist.shuffle = newState;
    const idx = playlists.findIndex((p) => p.id === currentPlaylist!.id);
    if (idx !== -1) playlists[idx] = { ...playlists[idx], shuffle: newState };
    toast.success(`Shuffle ${newState ? 'enabled' : 'disabled'}`);
  } catch (error) {
    console.error('Toggle shuffle error:', error);
    toast.error('Failed to update shuffle setting');
    playlistShuffleToggle.checked = !newState;
  } finally {
    playlistShuffleToggle.disabled = false;
  }
});

// Playlist settings - Delete playlist
deletePlaylistBtn.addEventListener('click', async () => {
  if (!currentPlaylist) return;
  if (!confirm(`Are you sure you want to delete "${currentPlaylist.name || currentPlaylist.id}"?`)) return;

  deletePlaylistBtn.classList.add('loading');
  try {
    const token = getStoredToken();
    await deletePlaylist(currentPlaylist.id, token);
    playlists = playlists.filter((p) => p.id !== currentPlaylist!.id);
    currentPlaylist = null;
    renderPlaylists();
    closePlaylistSettings();
    showView('playlists');
    toast.success('Playlist deleted');
  } catch (error) {
    console.error('Delete playlist error:', error);
    toast.error('Failed to delete playlist');
  } finally {
    deletePlaylistBtn.classList.remove('loading');
  }
});

// Playlist settings - Copy URL
copyPlaylistUrlBtn.addEventListener('click', async () => {
  if (!currentPlaylist) return;
  const url = getPlaylistDisplayUrl(currentPlaylist.id);
  try {
    await navigator.clipboard.writeText(url);
    copyPlaylistUrlBtn.classList.add('copied');
    setTimeout(() => copyPlaylistUrlBtn.classList.remove('copied'), 2000);
  } catch (error) {
    console.error('Failed to copy URL:', error);
  }
});

// Add Board modal
addBoardBtn.addEventListener('click', async () => {
  // Load boards if not already loaded
  if (boards.length === 0) {
    try {
      const token = getStoredToken();
      boards = await getBoards(token);
    } catch (error) {
      console.error('Failed to load boards:', error);
      toast.error('Failed to load boards');
      return;
    }
  }

  if (boards.length === 0) {
    toast.info('Create some boards first before adding them to playlists');
    return;
  }
  showAddBoardModal(true);
});

cancelAddBoardBtn.addEventListener('click', () => showAddBoardModal(false));

confirmAddBoardBtn.addEventListener('click', async () => {
  await handleAddBoardsToPlaylist();
});

addBoardModal.addEventListener('click', (e) => {
  if (e.target === addBoardModal) showAddBoardModal(false);
});

// Admin User Detail - Event Listeners
adminUserBackBtn.addEventListener('click', () => {
  window.location.hash = '';
  showView('admin');
});

adminImpersonateBtn.addEventListener('click', handleImpersonate);

adminDeleteUserBtn.addEventListener('click', () => {
  if (currentAdminUser) {
    handleDeleteUser(currentAdminUser.user.id);
  }
});

adminEditSubscriptionBtn.addEventListener('click', showEditSubscriptionModal);

cancelEditSubscriptionBtn.addEventListener('click', () => {
  editSubscriptionModal.classList.add('hidden');
});

confirmEditSubscriptionBtn.addEventListener('click', handleSaveSubscription);

editSubscriptionModal.addEventListener('click', (e) => {
  if (e.target === editSubscriptionModal) {
    editSubscriptionModal.classList.add('hidden');
  }
});

endImpersonationBtn.addEventListener('click', handleEndImpersonation);

// Check for impersonation on load
if (sessionStorage.getItem('admin_token')) {
  // We're in impersonation mode, show the banner
  impersonationBanner.classList.remove('hidden');
}

// Keyboard shortcuts
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') {
    showModal(false);
    showCreateApiKeyModal(false);
    apiKeyCreatedModal.classList.add('hidden');
    qrModal.classList.add('hidden');
    editSubscriptionModal.classList.add('hidden');
    showAddBoardModal(false);
    closeSettings();
    closePlaylistSettings();
    closeUserMenu();
    closeMobileSidebar();
  }
});

// Hero flap animation
function initHeroFlap() {
  const canvas = document.getElementById('hero-flap') as HTMLCanvasElement;
  if (!canvas) return;

  const ctx = canvas.getContext('2d')!;
  const dpr = window.devicePixelRatio || 1;
  const width = 120;
  const height = 180;

  canvas.width = width * dpr;
  canvas.height = height * dpr;
  ctx.scale(dpr, dpr);

  const charset = ' ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789.,!?:-&';
  const targetSymbols = ['S', 'P', 'L', 'I', 'T', '!', '?', '&'];
  let targetIndex = 0;
  let currentCharIndex = charset.indexOf('S');
  let targetCharIndex = charset.indexOf('S');
  let flipsRemaining = 0;
  let flipProgress = 0;
  let isFlipping = false;
  let lastFlipTime = performance.now();
  const flipInterval = 2500;
  const singleFlipDuration = 80;

  function drawCell(char: string, rotation = 0) {
    const padding = 6;
    const radius = 8;

    ctx.fillStyle = '#1a1a1a';
    ctx.beginPath();
    ctx.roundRect(0, 0, width, height, radius);
    ctx.fill();

    const flapWidth = width - padding * 2;
    const flapHeight = height - padding * 2;

    ctx.save();
    ctx.translate(width / 2, height / 2);

    if (rotation > 0) {
      const scale = Math.abs(Math.cos(rotation));
      ctx.scale(1, scale);
    }

    ctx.fillStyle = '#0a0a0a';
    ctx.beginPath();
    ctx.roundRect(-flapWidth / 2, -flapHeight / 2, flapWidth, flapHeight, 4);
    ctx.fill();

    const gradient = ctx.createLinearGradient(0, -flapHeight / 2, 0, flapHeight / 2);
    gradient.addColorStop(0, 'rgba(255, 255, 255, 0.05)');
    gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0)');
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0.1)');
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.roundRect(-flapWidth / 2, -flapHeight / 2, flapWidth, flapHeight, 4);
    ctx.fill();

    if (rotation === 0 || Math.sin(rotation) >= 0) {
      ctx.fillStyle = '#ffd700';
      ctx.font = '600 84px "Manrope", sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(char, 0, 4);
    }

    if (rotation === 0) {
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.7)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(-flapWidth / 2 + 4, 0);
      ctx.lineTo(flapWidth / 2 - 4, 0);
      ctx.stroke();
    }

    ctx.restore();
  }

  function animate(time: number) {
    if (!isFlipping && time - lastFlipTime > flipInterval) {
      isFlipping = true;
      flipProgress = 0;
      targetIndex = (targetIndex + 1) % targetSymbols.length;
      targetCharIndex = charset.indexOf(targetSymbols[targetIndex]);
      flipsRemaining = (targetCharIndex - currentCharIndex + charset.length) % charset.length;
      if (flipsRemaining === 0) flipsRemaining = charset.length;
    }

    ctx.clearRect(0, 0, width, height);

    if (isFlipping && flipsRemaining > 0) {
      flipProgress += 16 / singleFlipDuration;

      if (flipProgress >= 1) {
        flipProgress = 0;
        currentCharIndex = (currentCharIndex + 1) % charset.length;
        flipsRemaining--;

        if (flipsRemaining === 0) {
          isFlipping = false;
          lastFlipTime = time;
        }
        drawCell(charset[currentCharIndex], 0);
      } else {
        const rotation = flipProgress * Math.PI;
        const nextCharIndex = (currentCharIndex + 1) % charset.length;
        const showingBack = Math.sin(rotation) < 0;
        const displayChar = showingBack ? charset[nextCharIndex] : charset[currentCharIndex];
        drawCell(displayChar, rotation);
      }
    } else {
      drawCell(charset[currentCharIndex], 0);
    }

    requestAnimationFrame(animate);
  }

  requestAnimationFrame(animate);
}

// Hash routing
window.addEventListener('hashchange', handleHashChange);

// Initialize
initHeroFlap();
init();
initCookieConsent();
